/**
 * Mobile Menu Auto-Open Functionality
 *
 * This script automatically opens the parent menu when the user is on a page
 * that belongs to a submenu category. It detects the current page and opens
 * the corresponding parent menu in the mobile navigation.
 */

class MobileMenuAutoOpen {
  constructor() {
    this.currentUrl = window.location.href;
    this.currentPath = window.location.pathname;
    this.menuDrawer = null;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.menuDrawer = document.querySelector('header-drawer');
    if (!this.menuDrawer) return;

    this.menuDrawer.addEventListener('click', (event) => {
      const closeBtn = event.target.closest('.menu-drawer__close-button');
      if (!closeBtn) return;
      const parentSubmenu = closeBtn.closest('.menu-drawer__submenu');
      if (parentSubmenu && parentSubmenu.closest('.menu-drawer__submenu')) {
        const parentDetails = closeBtn.closest('details');
        if (parentDetails) {
          event.preventDefault();
          parentDetails.removeAttribute('open');
        }
      }
    });

    const mainDetails = document.querySelector('#Details-menu-drawer-container');
    if (mainDetails) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === 'open' && mainDetails.hasAttribute('open')) {
            setTimeout(() => {
              this.openShoppingMenu();
              this.autoOpenCurrentMenu();
            }, 50);
          }
        });
      });
      observer.observe(mainDetails, { attributes: true });
    }

    this.menuDrawer.addEventListener('menu-drawer-opened', () => {
      this.openShoppingMenu();
      this.autoOpenCurrentMenu();
    });

    this.menuDrawer.addEventListener('openMenuDrawer', () => {
      setTimeout(() => {
        this.openShoppingMenu();
        this.autoOpenCurrentMenu();
      }, 300);
    });
  }

  openShoppingMenu() {
    const shoppingDetails = this.menuDrawer.querySelector('details[data-menu-handle="shop"]');
    if (shoppingDetails && !shoppingDetails.hasAttribute('open')) {
      shoppingDetails.setAttribute('open', '');
    }
  }

  autoOpenCurrentMenu() {
    // Get all menu items with their URLs
    const menuItems = this.getAllMenuItems();

    // Find the current page in the menu structure
    const currentMenuItem = this.findCurrentMenuItem(menuItems);

    if (currentMenuItem) {
      this.openParentMenus(currentMenuItem);
    }
  }

  getAllMenuItems() {
    const menuItems = [];

    // Get main menu items
    const mainMenuItems = this.menuDrawer.querySelectorAll('[data-menu-url]');
    mainMenuItems.forEach(item => {
      const url = item.getAttribute('data-menu-url');
      const handle = item.getAttribute('data-menu-handle');
      const type = item.tagName === 'DETAILS' ? 'parent' : 'link';

      menuItems.push({
        element: item,
        url: url,
        handle: handle,
        type: type,
        level: 0
      });
    });

    // Get child menu items
    const childMenuItems = this.menuDrawer.querySelectorAll('[data-child-url]');
    childMenuItems.forEach(item => {
      const url = item.getAttribute('data-child-url');
      const handle = item.getAttribute('data-child-handle');
      const parentDetails = item.closest('details[data-menu-url]');

      if (parentDetails) {
        menuItems.push({
          element: item,
          url: url,
          handle: handle,
          type: 'link',
          level: 1,
          parent: parentDetails
        });
      }
    });

    // Get grandchild menu items
    const grandchildMenuItems = this.menuDrawer.querySelectorAll('[data-grandchild-url]');
    grandchildMenuItems.forEach(item => {
      const url = item.getAttribute('data-grandchild-url');
      const handle = item.getAttribute('data-grandchild-handle');
      const childDetails = item.closest('details[data-child-menu-url]');
      const parentDetails = childDetails ? childDetails.closest('details[data-menu-url]') : null;

      if (childDetails && parentDetails) {
        menuItems.push({
          element: item,
          url: url,
          handle: handle,
          type: 'link',
          level: 2,
          parent: childDetails,
          grandparent: parentDetails
        });
      }
    });

    return menuItems;
  }

  findCurrentMenuItem(menuItems) {
    // First, try to find an exact URL match
    let currentMenuItem = menuItems.find(item =>
      item.url === this.currentUrl ||
      item.url === this.currentPath ||
      this.currentUrl.includes(item.url) ||
      this.currentPath.includes(item.url)
    );

    // If no exact match, try to find by handle in the URL
    if (!currentMenuItem) {
      currentMenuItem = menuItems.find(item => {
        if (!item.handle) return false;

        // Check if the handle is in the current URL path
        const urlParts = this.currentPath.split('/');
        return urlParts.some(part => part === item.handle);
      });
    }

    // If still no match, try to find by collection handle from meta tags
    if (!currentMenuItem) {
      const collectionHandle = this.getCollectionHandleFromMeta();
      if (collectionHandle) {
        currentMenuItem = menuItems.find(item => item.handle === collectionHandle);
      }
    }

    // If still no match, try to find by product handle from meta tags
    if (!currentMenuItem) {
      const productHandle = this.getProductHandleFromMeta();
      if (productHandle) {
        currentMenuItem = menuItems.find(item => item.handle === productHandle);
      }
    }

    // If still no match, try to find by page template
    if (!currentMenuItem) {
      currentMenuItem = this.findMenuItemByTemplate(menuItems);
    }

    return currentMenuItem;
  }

  getCollectionHandleFromMeta() {
    // Try to get collection handle from meta tags
    const collectionMeta = document.querySelector('meta[property="og:url"]');
    if (collectionMeta) {
      const url = collectionMeta.getAttribute('content');
      if (url && url.includes('/collections/')) {
        const match = url.match(/\/collections\/([^\/]+)/);
        if (match) return match[1];
      }
    }

    // Try to get from breadcrumbs
    const breadcrumbLinks = document.querySelectorAll('.breadcrumb a[href*="/collections/"]');
    for (const link of breadcrumbLinks) {
      const href = link.getAttribute('href');
      const match = href.match(/\/collections\/([^\/]+)/);
      if (match) return match[1];
    }

    // Try to get from page URL structure
    if (this.currentPath.includes('/collections/')) {
      const match = this.currentPath.match(/\/collections\/([^\/]+)/);
      if (match) return match[1];
    }

    return null;
  }

  getProductHandleFromMeta() {
    // Try to get product handle from meta tags
    const productMeta = document.querySelector('meta[property="og:url"]');
    if (productMeta) {
      const url = productMeta.getAttribute('content');
      if (url && url.includes('/products/')) {
        const match = url.match(/\/products\/([^\/]+)/);
        if (match) return match[1];
      }
    }

    // Try to get from page URL structure
    if (this.currentPath.includes('/products/')) {
      const match = this.currentPath.match(/\/products\/([^\/]+)/);
      if (match) return match[1];
    }

    return null;
  }

  findMenuItemByTemplate(menuItems) {
    // Check if we're on a collection page by looking for collection-specific elements
    if (document.querySelector('.collection-hero__title') ||
        document.querySelector('.collection') ||
        this.currentPath.includes('/collections/')) {

      // Try to find menu item that matches collection context
      return menuItems.find(item => {
        // Look for collection-related keywords in the menu title
        const title = item.element.textContent.toLowerCase();
        const collectionKeywords = ['coleção', 'collection', 'categoria', 'category'];
        return collectionKeywords.some(keyword => title.includes(keyword));
      });
    }

    // Check if we're on a product page
    if (document.querySelector('.product__title') ||
        document.querySelector('.product') ||
        this.currentPath.includes('/products/')) {

      // Try to find menu item that matches product context
      return menuItems.find(item => {
        const title = item.element.textContent.toLowerCase();
        const productKeywords = ['produto', 'product', 'loja', 'shop'];
        return productKeywords.some(keyword => title.includes(keyword));
      });
    }

    return null;
  }

  openParentMenus(currentMenuItem) {
    if (!currentMenuItem) return;

    // Open parent menu if this is a child item
    if (currentMenuItem.level > 0 && currentMenuItem.parent) {
      this.openMenuDetails(currentMenuItem.parent);
    }

    // Open grandparent menu if this is a grandchild item
    if (currentMenuItem.level === 2 && currentMenuItem.grandparent) {
      this.openMenuDetails(currentMenuItem.grandparent);
    }
  }

  openMenuDetails(detailsElement) {
    if (!detailsElement || detailsElement.hasAttribute('open')) return;

    // Open the details element
    detailsElement.setAttribute('open', '');

    // Add necessary classes for animation
    detailsElement.classList.add('menu-opening');

    // Find the parent menu element and add submenu-open class
    const parentMenuElement = detailsElement.closest('.has-submenu');
    if (parentMenuElement) {
      parentMenuElement.classList.add('submenu-open');
    }

    // Set aria-expanded on the summary
    const summary = detailsElement.querySelector('summary');
    if (summary) {
      summary.setAttribute('aria-expanded', 'true');
    }

    // Remove the menu-opening class after animation
    setTimeout(() => {
      detailsElement.classList.remove('menu-opening');
    }, 300);
  }

  // Helper method to check if we're on a collection page
  isCollectionPage() {
    return this.currentPath.includes('/collections/') ||
           document.querySelector('.collection-hero__title') ||
           document.querySelector('.collection');
  }

  // Helper method to check if we're on a product page
  isProductPage() {
    return this.currentPath.includes('/products/') ||
           document.querySelector('.product__title') ||
           document.querySelector('.product');
  }
}

// Initialize the mobile menu auto-open functionality
document.addEventListener('DOMContentLoaded', () => {
  new MobileMenuAutoOpen();
});

// Also initialize if the script is loaded after DOM is ready
if (document.readyState !== 'loading') {
  new MobileMenuAutoOpen();
}
